import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import {
  ArrowRight, BookOpen, Mic, Briefcase, MessageCircle, Send, CheckCircle2, Loader2,
  AtSign, Globe, Sparkles, Heart,
} from 'lucide-react';
import AnimatedStat from '@/components/AnimatedStat';

const HIGHLIGHT_ICONS = { AUTHOR: BookOpen, SPEAKER: Mic, ENTREPRENEUR: Briefcase, COACH: MessageCircle };

// Inline brand SVGs — keeps everything on-brand without relying on social-icon libraries.
const SOCIAL_GLYPH = ({ icon }) => {
  const i = (icon || '').toLowerCase();
  const cls = "w-[22px] h-[22px]";
  if (i === 'instagram') return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>
  );
  if (i === 'facebook') return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 1 0-11.5 9.87v-6.99H8v-2.88h2.5V9.8c0-2.46 1.46-3.83 3.7-3.83 1.07 0 2.2.2 2.2.2v2.42h-1.24c-1.22 0-1.6.76-1.6 1.54v1.85h2.72l-.43 2.88h-2.29v6.99A10 10 0 0 0 22 12Z"/></svg>
  );
  if (i === 'youtube') return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.5a3 3 0 0 0-2.1-2.13C19.5 3.9 12 3.9 12 3.9s-7.5 0-9.4.47A3 3 0 0 0 .5 6.5C0 8.4 0 12 0 12s0 3.6.5 5.5a3 3 0 0 0 2.1 2.13C4.5 20.1 12 20.1 12 20.1s7.5 0 9.4-.47a3 3 0 0 0 2.1-2.13C24 15.6 24 12 24 12s0-3.6-.5-5.5ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z"/></svg>
  );
  if (i === 'linkedin') return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M20.45 20.45h-3.55v-5.56c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.66H9.36V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.36-1.85c3.6 0 4.27 2.37 4.27 5.45v6.29ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13ZM7.12 20.45H3.56V9h3.56v11.45Z"/></svg>
  );
  if (i === 'tiktok') return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M19.6 6.32a5.36 5.36 0 0 1-3.84-1.62 5.36 5.36 0 0 1-1.55-3.7h-3.62v13.3a3.04 3.04 0 1 1-2.14-2.9V7.7a6.66 6.66 0 1 0 5.76 6.6V8.9a8.97 8.97 0 0 0 5.39 1.78v-3.5a5.45 5.45 0 0 1 0-.86Z"/></svg>
  );
  if (i === 'x' || i === 'twitter') return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.23Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/></svg>
  );
  return <Globe className={cls} />;
};

export default function AboutPage() {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', organization: '', inquiry_type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/about').then(r => {
      setC(r.data);
      setForm(f => ({ ...f, inquiry_type: r.data.inquiry_types?.[0] || 'General' }));
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { setErr('Name, email, and a short message are required.'); return; }
    setSubmitting(true);
    try {
      await api.post('/inquiries', form);
      setSubmitted(true);
    } catch (e2) { setErr(e2?.response?.data?.detail || 'Could not send. Try again.'); }
    finally { setSubmitting(false); }
  };

  if (!c) return <div className="min-h-screen bg-white" />;

  return (
    <div className="bg-white text-ink">
      {/* HERO */}
      <section className="relative bg-ink text-white py-16 md:py-24 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(700px 380px at 80% 50%, rgba(212,164,55,0.18), transparent 60%)' }} />
        <div className="relative kc-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">
              <Sparkles size={12}/> {c.eyebrow}
            </div>
            <h1 className="mt-4 font-display uppercase leading-[0.9]" style={{ fontSize: 'clamp(2.5rem, 7vw, 6rem)' }} data-testid="about-title">
              {c.title_top}<br /><span className="text-[color:var(--kc-gold)]">{c.title_bottom}</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed">{c.subtitle}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-[var(--kc-radius-md)] overflow-hidden border border-white/10 shadow-2xl">
              <img src={c.portrait_image} alt="Mickey Trivett" className="w-full aspect-[4/5] object-cover" data-testid="about-portrait"/>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-4">
                <div className="font-display uppercase text-xl">Mickey Trivett</div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/70">Author · Speaker · Coach</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS STRIP */}
      <section className="bg-white py-10 border-b border-kc" data-testid="about-highlights">
        <div className="kc-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {(c.highlights || []).map((h, i) => {
            const Icon = HIGHLIGHT_ICONS[h.title] || Sparkles;
            return (
              <div key={i} className="group bg-surface-2 hover:bg-white border border-kc hover:border-[color:var(--kc-gold)] rounded-[var(--kc-radius-md)] p-5 transition-all hover:-translate-y-0.5">
                <div className="w-11 h-11 rounded-full bg-ink text-white group-hover:bg-[color:var(--kc-gold)] group-hover:text-black flex items-center justify-center transition-colors"><Icon size={20}/></div>
                <div className="mt-3 font-display uppercase text-xl tracking-wider">{h.title}</div>
                <p className="mt-1 text-sm text-ink-2 leading-relaxed">{h.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* STORY */}
      <section className="bg-white py-14 md:py-20" data-testid="about-story">
        <div className="kc-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <div className="sticky top-20">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">My Story</div>
              <h2 className="mt-2 font-display uppercase leading-[0.95]" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>The road that built the work.</h2>
            </div>
          </div>
          <div className="lg:col-span-9 space-y-5 max-w-3xl">
            {(c.story || []).map((p, i) => (
              <p key={i} className="text-lg text-ink-2 leading-relaxed first:text-xl first:font-display first:uppercase first:tracking-wide first:text-ink first:leading-tight">{p}</p>
            ))}
            <div className="mt-6 inline-block px-5 py-3 bg-ink text-white rounded-[var(--kc-radius-md)] font-display uppercase tracking-[0.18em] text-base md:text-lg" data-testid="about-motto">
              {(c.motto || '').split(/[.\s]+/).filter(Boolean).map((w, i, arr) => (
                <React.Fragment key={i}>
                  <span className={i === 1 ? 'text-[color:var(--kc-gold)]' : ''}>{w}.</span>{i < arr.length - 1 ? ' ' : ''}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative bg-ink text-white py-16 md:py-20 overflow-hidden" data-testid="about-stats">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50"/>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(600px 320px at 50% 50%, rgba(212,164,55,0.12), transparent 60%)' }}/>
        <div className="relative kc-container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">By the numbers</div>
            <h2 className="mt-2 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>The receipts.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-5xl mx-auto">
            {(c.stats || []).map((s, i) => (
              <AnimatedStat key={i} value={s.value} suffix={s.suffix} label={s.label} delay={i * 150}/>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIALS */}
      <section className="bg-white py-14 md:py-20" data-testid="about-socials">
        <div className="kc-container">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">{c.socials_eyebrow}</div>
            <h2 className="mt-2 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>Follow along.</h2>
            <p className="mt-3 text-lg text-ink-2">{c.socials_body}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 max-w-4xl mx-auto">
            {(c.socials || []).map((s, i) => {
              return (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="group bg-surface-2 hover:bg-ink hover:text-white border border-kc hover:border-ink rounded-[var(--kc-radius-md)] p-4 text-center transition-all hover:-translate-y-1" data-testid={`social-${s.icon}`}>
                  <div className="mx-auto text-[color:var(--kc-gold-2)] group-hover:text-[color:var(--kc-gold)] transition-colors inline-flex"><SOCIAL_GLYPH icon={s.icon}/></div>
                  <div className="mt-2 font-display uppercase text-sm tracking-wider">{s.label}</div>
                  <div className="mt-0.5 text-[10px] text-muted-kc group-hover:text-white/70 transition-colors">{s.handle}</div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* BOOK ME FORM */}
      <section className="relative bg-ink text-white py-16 md:py-24 overflow-hidden" data-testid="about-bookme">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50"/>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(700px 380px at 20% 50%, rgba(212,164,55,0.15), transparent 60%)' }}/>
        <div className="relative kc-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">
              <Heart size={12}/> {c.book_me_eyebrow}
            </div>
            <h2 className="mt-4 font-display uppercase leading-[0.9]" style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }} data-testid="about-bookme-title">{c.book_me_title}</h2>
            <p className="mt-4 text-lg text-white/80 leading-relaxed">{c.book_me_body}</p>
            <div className="mt-6 space-y-2 text-sm text-white/65">
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[color:var(--kc-gold)]"/> Speaking engagements & keynotes</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[color:var(--kc-gold)]"/> 1:1 coaching availability</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[color:var(--kc-gold)]"/> Podcast & media requests</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[color:var(--kc-gold)]"/> Or just to say hi — that works too</div>
            </div>
          </div>
          <div className="lg:col-span-7">
            {submitted ? (
              <div className="bg-white text-ink rounded-[var(--kc-radius-md)] p-8 md:p-10 text-center" data-testid="inquiry-success">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[color:var(--kc-gold)] text-black mb-4"><CheckCircle2 size={28}/></div>
                <h3 className="font-display uppercase text-3xl">Got it.</h3>
                <p className="mt-3 text-ink-2">Thanks for reaching out. I (or my team) will get back to you personally — usually within a few days.</p>
                <Link to="/" className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold">Back to home</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white text-ink rounded-[var(--kc-radius-md)] p-6 md:p-8 space-y-4" data-testid="inquiry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Your name *" value={form.name} onChange={(v) => setForm({...form, name: v})} testid="inq-name"/>
                  <Field label="Email *" value={form.email} onChange={(v) => setForm({...form, email: v})} type="email" testid="inq-email"/>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Phone (optional)" value={form.phone} onChange={(v) => setForm({...form, phone: v})} testid="inq-phone"/>
                  <Field label="Organization (optional)" value={form.organization} onChange={(v) => setForm({...form, organization: v})} testid="inq-org"/>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1">What are you reaching out about?</label>
                  <select value={form.inquiry_type} onChange={(e) => setForm({...form, inquiry_type: e.target.value})} className="w-full h-11 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="inq-type">
                    {(c.inquiry_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Message *</label>
                  <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={5} placeholder="Tell me a bit about what you have in mind — dates, audience, goals, or just what's on your mind." className="w-full p-3 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="inq-message"/>
                </div>
                {err && <div className="text-sm text-[color:var(--kc-danger)]" data-testid="inq-error">{err}</div>}
                <button type="submit" disabled={submitting} className="w-full h-13 inline-flex items-center justify-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-50 transition-colors text-base" data-testid="inq-submit">
                  {submitting ? <><Loader2 size={16} className="animate-spin"/> Sending</> : <>Send my message <Send size={16}/></>}
                </button>
                <p className="text-[10px] text-muted-kc text-center">Your message goes straight to Mickey's team. No spam. No list-sharing.</p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white py-12">
        <div className="kc-container text-center max-w-2xl mx-auto">
          <h3 className="font-display uppercase text-2xl md:text-3xl">Want to do the work?</h3>
          <p className="mt-2 text-ink-2">Start with the HEADSPACE course — pay what you want.</p>
          <Link to="/courses/headspace" className="mt-4 inline-flex items-center gap-2 h-12 px-6 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors" data-testid="about-course-cta">Start the course <ArrowRight size={16}/></Link>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-11 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid={testid}/>
  </div>
);
